import React, { useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import AddButton from "@Components/Buttons/AddButton";
import ListingPageTemplate from "@Pages/ListingPageTemplate/ListingPageTemplate";
import themeCreator from "@Themes/themeCreator";
import useDeleteItem from "@Hooks/useDeleteItem";
import { useDispatch } from "react-redux";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import {
  SAVE_DOCUMENT,
  DELETE_ORDER,
  EXPORT_PAYMENT_XSLS,
  GET_ALL_INVOICES_URL,
} from "@API/APIRoutes";
import { DeletePopup } from "@Components/Popup/DeletePopup";
import PaymentIcon from "@mui/icons-material/Payment";
import VerifiedIcon from "@mui/icons-material/Verified";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { fontSize, ThemeProvider } from "@mui/system";
import usePostItem from "@Hooks/usePostItem";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import axios from "axios";
import usePaginate from "@Hooks/usePaginate";
import {
  getGridStringOperators,
  getGridNumericOperators,
} from "@mui/x-data-grid";
import axiosInstance from "@API/axios";
import { Snackbar, Alert } from "@mui/material";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { encryptId } from "../../Utils/SecurityFunctions";

function PaymentsListing() {
  const theme = themeCreator();
  const error_message = `Failed to Fetch Invoices Trying Again!`;
  const [selectedId, setSelectedId] = useState(null);
  const [showExportPopup, setShowExportPopup] = useState(false);
  // Add these state declarations near your other useState declarations:
  const [isDownloading, setIsDownloading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");
  const navigate = useNavigate();
  const closeExportPopup = () => setShowExportPopup(false);
  const openExportPopup = () => setShowExportPopup(true);
  const exportCallBack = () => {
    setShowExportPopup(false);
  };
  //********************* PAGINATION CONTROL *********************//
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const { loading: pageLoading, getPage } = usePaginate();

  const fetchPage = (queryOptions, filterModel) => {
    let filterItems = filterModel.items ? [...filterModel.items] : [];

    filterItems = filterItems.map((filterItem) => {
      if (filterItem && typeof filterItem.value === "string") {
        return { ...filterItem, value: filterItem.value.trim() };
      }
      return filterItem;
    });
    getPage(
      GET_ALL_INVOICES_URL,
      error_message,
      queryOptions,
      filterItems
    ).then((data) => {
      setRows(data.content);
      setRowCount(data.totalElements);
    });
  };

  //********************* CANCEL POPUP *********************//
  const [showCancelPopup, setShowCancelPopup] = useState(false);

  const { loading: cancelOrderLoading, deleteItem: cancelOrder } =
    useDeleteItem();

  const closePopup = () => setShowCancelPopup(false);

  const openDeletePopup = (OrderId) => {
    setSelectedId(OrderId);
    setShowCancelPopup(true);
  };

  const confirmCancelCallBack = (canceledOrderId) => {
    setRows((prevData) =>
      prevData.filter((Order) => Order.id !== canceledOrderId)
    );
    closePopup();
  };

  const ConfirmCancel = (OrderId) => {
    cancelOrder(
      OrderId,
      confirmCancelCallBack,
      DELETE_ORDER,
      `Order Canceled Successfully!`,
      `Failed to Cancel Order!`
    );
  };
  const formatDate = (date) => {
    if (!date) return "N/A";

    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString("en-GB");
  };

  const downloadCSV = (id) => {
    setShowExportPopup(false);
    // Set downloading state to true to show snackbar
    setIsDownloading(true);

    // Determine the export URL based on the order state
    let exportUrl = EXPORT_PAYMENT_XSLS;
    let filterModel;
    filterModel = JSON.parse(sessionStorage.getItem("_payments_filterState"));
    return axiosInstance
      .post(exportUrl, filterModel?.items || [], {
        responseType: "blob",
      })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.setAttribute("download", "orders.xlsx");
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
        // Set downloading state to false to hide snackbar
        setIsDownloading(false);
      })
      .catch((error) => {
        console.error("Excel download failed:", error);
        // Set downloading state to false in case of error
        setIsDownloading(false);
        setSnackbarMessage("Excel download failed");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      });
  };
  //********************* TABLE PROPS *********************//

  // const meatballsMenuOptions = (id, releasable, poNumber) => [
  //   {
  //     label: "Add Invoice",
  //     icon: <PaymentIcon />,
  //     color: "primary",
  //     action: () =>
  //       navigate(`${DefaultRoutes.payments}`),
  //   },
  //   {
  //     label: "Add Approval",
  //     icon: <VerifiedIcon />,
  //     color: "primary",
  //     action: () => navigate(DefaultRoutes.approval.replace(":orderId", id)),
  //   },
  //   {
  //     label: "Add CargoX",
  //     icon: <LocalShippingIcon />,
  //     color: "primary",
  //     action: () => navigate(DefaultRoutes.cargox.replace(":orderId", id)),
  //   },
  // ];

  const stringCustomOperators = getGridStringOperators().filter((operator) =>
    [
      "contains",
      "doesNotContain",
      "equals",
      "doesNotEqual",
      "startsWith",
      "endsWith",
      "isEmpty",
      "isNotEmpty",
    ]?.includes(operator.value)
  );

  const numberCustomOperators = getGridNumericOperators().filter((operator) =>
    ["=", "!=", ">", ">=", "<", "<=", "isEmpty", "isNotEmpty"]?.includes(
      operator.value
    )
  );
  const formatState = (state) => {
    if (!state) return "";
    return state
      .toLowerCase() // Convert to lowercase
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };

  const columns = [
    {
      field: "state",
      headerName: "State",
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
      renderCell: (params) => {
        const isReadyToRelease = params.row.state === "RELEASED";
        return (
          <span
            style={{
              color: isReadyToRelease ? "green" : "grey",
              fontStyle: isReadyToRelease ? "normal" : "italic",
              display: "flex",
              alignItems: "center",
            }}
          >
            {"•"} {formatState(params.row.state)}
          </span>
        );
      },
    },
    {
      field: "poNumber",
      headerName: "PO Number",
      flex: 2,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "agentName",
      headerName: "Agent",
      flex: 2,
      type: "string",
      renderCell: (params) => (
        <div>
          <div>{params.row.agentName || "N/A"}</div>
        </div>
      ),
      sortable: false,
      filterOperators: stringCustomOperators,
    },
    {
      field: "transactionNumber",
      headerName: "Transaction Number",
      flex: 3,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "transactionType",
      headerName: "Transaction Type",
      flex: 2.75,
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "coraId",
      headerName: "Cora Id",
      flex: 2,
      filterOperators: stringCustomOperators,
    },
    {
      field: "totalInvoiceCost",
      headerName: "Total Cost",
      flex: 2,
      filterOperators: numberCustomOperators,
    },
    {
      field: "count_of_payments",
      headerName: "No. of Payments",
      sortable: false,
      flex: 2,
      filterOperators: numberCustomOperators,
    },
    {
      field: "releasedDate",
      headerName: "Release Date",
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "submissionDate",
      headerName: "Submission Date",
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 2,
      type: "date",
      valueFormatter: (params) => formatDate(params),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      flex: 2.5,
      hideable: false,
      renderCell: (params) => (
        <ThemeProvider theme={theme}>
          <Tooltip title="Preview Payment">
            <IconButton
              color="info"
              onClick={() => {
                navigate(
                  DefaultRoutes.paymentPreview.replace(
                    ":invoiceId",
                    encryptId(params.row.id)
                  )
                );
              }}
              size="small"
            >
              <InfoRoundedIcon />
            </IconButton>
          </Tooltip>
          {params.row.orderId && (
            <Tooltip title="Go To Order">
              <IconButton
                color="warning"
                onClick={() => {
                  navigate(
                    DefaultRoutes.orderInfo.replace(
                      ":orderId",
                      encryptId(params.row.orderId)
                    )
                  );
                }}
                size="small"
              >
                <ArrowRightAltIcon />
              </IconButton>
            </Tooltip>
          )}
        </ThemeProvider>
      ),
    },
  ];

  const initialColumnVisibilityModel = {
    releaseDate: true,
    totalInvoiceCost: true,
    submissionDate: false,
    coraId: false,
  };

  const PaymentsTableProps = {
    columns,
    disableRowSelectionOnClick: true,
    disableColumnMenu: true,
    initialColumnVisibilityModel,
    rowCount,
    rows,
    isLoading: pageLoading,
    fetchPage,
    exportUrl: EXPORT_PAYMENT_XSLS,
    exportOnClick: openExportPopup,
    sortBy: "submissionDate",
  };

  const handleAddTransactionButton = () => {
    navigate(DefaultRoutes.invoice);
  };

  //********************* RETURN *********************//
  return (
    <Box>
      <ListingPageTemplate
        title={"Payments Listing"}
        PageIcon={<AccountBalanceIcon color="white" fontSize="large" />}
        iconColor={theme.palette.primary.main}
        buttons={[
          <AddButton
            key="add-button"
            ButtonText={`Add Transaction`}
            onClick={handleAddTransactionButton}
          />,
        ]}
        tableProps={PaymentsTableProps}
        serverPagination={true}
      />

      {DeletePopup(
        "Export",
        showExportPopup,
        closeExportPopup,
        downloadCSV,
        null,
        false,
        "Export"
      )}
      <Snackbar
        open={isDownloading || openSnackbar}
        autoHideDuration={openSnackbar ? 6000 : null}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={isDownloading ? "info" : snackbarSeverity}
          onClose={() => (isDownloading ? null : setOpenSnackbar(false))}
        >
          {isDownloading ? "Downloading Excel..." : snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentsListing;
